import intelliJetNetService, { QueryBuilder } from '../intellijetnet'
import { Event } from '../../types/intellijetnet'

export class EventsService {
  private static readonly TABLE_NAME = 'events'

  /**
   * Get query builder for events
   */
  static query(): QueryBuilder {
    return intelliJetNetService.from(this.TABLE_NAME)
  }

  /**
   * Get event by ID
   */
  static async getById(eventId: string): Promise<Event | null> {
    const { data } = await this.query()
      .eq('event_id', eventId)
      .execute<Event>()
    return data[0] || null
  }

  /**
   * Get event by ID with fallback for legacy numeric IDs
   * This method attempts to handle both UUID and numeric event IDs
   */
  static async getByIdWithFallback(eventId: string): Promise<Event | null> {
    try {
      // If the ID looks numeric, try to find the event by treating it as a numeric ID
      // This handles the case where the database actually contains numeric IDs
      if (/^\d+$/.test(eventId)) {
        // Event ID appears to be a numeric ID - attempting lookup

        // Try to query by treating the numeric string as the actual event_id
        // This works if the database column actually contains numeric values
        const { data } = await this.query()
          .eq('event_id', eventId)
          .execute<Event>()

        if (data && data.length > 0) {
          return data[0]
        }

        // If that doesn't work, the numeric ID might not exist in the database
        return null
      }

      // For UUID format, use the standard lookup
      return await this.getById(eventId)
    } catch (error) {
      return null
    }
  }

  /**
   * Get events by aircraft ID
   */
  static async getByAircraftId(aircraftId: number): Promise<Event[]> {
    const { data } = await this.query()
      .eq('aircraft_id', aircraftId)
      .eq('is_active', true)
      .order('date', false)
      .execute<Event>()
    return data
  }

  /**
   * Get events by company ID
   */
  static async getByCompanyId(companyId: number): Promise<Event[]> {
    const { data } = await this.query()
      .eq('company_id', companyId)
      .eq('is_active', true)
      .order('date', false)
      .execute<Event>()
    return data
  }

  /**
   * Get events by contact ID
   */
  static async getByContactId(contactId: number): Promise<Event[]> {
    const { data } = await this.query()
      .eq('contact_id', contactId)
      .eq('is_active', true)
      .order('date', false)
      .execute<Event>()
    return data
  }

  /**
   * Get events by model ID
   */
  static async getByModelId(modelId: number): Promise<Event[]> {
    const { data } = await this.query()
      .eq('model_id', modelId)
      .eq('is_active', true)
      .order('date', false)
      .execute<Event>()
    return data
  }

  /**
   * Get events by date range
   */
  static async getByDateRange(startDate: string, endDate: string): Promise<Event[]> {
    const { data } = await this.query()
      .gte('date', startDate)
      .lte('date', endDate)
      .eq('is_active', true)
      .order('date', false)
      .execute<Event>()
    return data
  }

  /**
   * Search events by subject or description
   */
  static async search(searchTerm: string): Promise<Event[]> {
    const { data } = await this.query()
      .or(
        [
          { column: 'subject', operator: 'ilike', value: `%${searchTerm}%` },
          { column: 'description', operator: 'ilike', value: `%${searchTerm}%` }
        ]
      )
      .eq('is_active', true)
      .order('date', false)
      .limit(100)
      .execute<Event>()
    return data
  }

  /**
   * Get recent events
   */
  static async getRecent(days = 30, limit = 50): Promise<Event[]> {
    const date = new Date()
    date.setDate(date.getDate() - days)

    const { data } = await this.query()
      .gte('date', date.toISOString())
      .eq('is_active', true)
      .order('date', false)
      .limit(limit)
      .execute<Event>()
    return data
  }

  /**
   * Get upcoming events
   */
  static async getUpcoming(days = 30): Promise<Event[]> {
    const today = new Date()
    const futureDate = new Date()
    futureDate.setDate(today.getDate() + days)

    const { data } = await this.query()
      .gte('date', today.toISOString())
      .lte('date', futureDate.toISOString())
      .eq('is_active', true)
      .order('date', true)
      .execute<Event>()
    return data
  }

  /**
   * Get events with related data
   */
  static async getWithRelatedData(eventId: string): Promise<any> {
    const { data } = await this.query()
      .select(`
        *,
        aircraft(*),
        companies(*),
        contacts(*),
        aircraft_models(*)
      `)
      .eq('event_id', eventId)
      .execute<Event>()
    return data[0]
  }

  /**
   * Get events with pagination
   */
  static async getPaginated(params: {
    page?: number
    limit?: number
    aircraftId?: number
    companyId?: number
    contactId?: number
    startDate?: string
    endDate?: string
    search?: string
  } = {}): Promise<{ data: Event[]; total: number }> {
    const page = params.page || 1
    const limit = params.limit || 50
    const offset = (page - 1) * limit

    let query = this.query()
      .eq('is_active', true)
      .order('date', false)
      .limit(limit)
      .offset(offset)
      .count('exact')

    if (params.aircraftId) {
      query = query.eq('aircraft_id', params.aircraftId)
    }
    if (params.companyId) {
      query = query.eq('company_id', params.companyId)
    }
    if (params.contactId) {
      query = query.eq('contact_id', params.contactId)
    }
    if (params.startDate) {
      query = query.gte('date', params.startDate)
    }
    if (params.endDate) {
      query = query.lte('date', params.endDate)
    }
    if (params.search) {
      query = query.or(
        [
          { column: 'subject', operator: 'ilike', value: `%${params.search}%` },
          { column: 'description', operator: 'ilike', value: `%${params.search}%` }
        ]
      )
    }

    const result = await query.execute<Event>()
    return {
      data: result.data,
      total: result.count || 0
    }
  }

  /**
   * Get event statistics
   */
  static async getStatistics(startDate?: string, endDate?: string): Promise<{
    total: number
    byAircraft: number
    byCompany: number
    byContact: number
  }> {
    let query = this.query().eq('is_active', true)

    if (startDate) query = query.gte('date', startDate)
    if (endDate) query = query.lte('date', endDate)

    const { count: total } = await query
      .count('exact')
      .execute<Event>()

    // Count unique aircraft
    const { data: aircraftData } = await query
      .select('aircraft_id')
      .execute<{ aircraft_id: number }>()
    const uniqueAircraft = new Set(aircraftData.map(e => e.aircraft_id)).size

    // Count events with companies
    const { count: withCompany } = await query
      .neq('company_id', null)
      .count('exact')
      .execute<Event>()

    // Count events with contacts
    const { count: withContact } = await query
      .neq('contact_id', null)
      .count('exact')
      .execute<Event>()

    return {
      total: total || 0,
      byAircraft: uniqueAircraft,
      byCompany: withCompany || 0,
      byContact: withContact || 0
    }
  }

  /**
   * Create new event
   */
  static async create(event: Omit<Event, 'event_id' | 'sync_timestamp' | 'is_active'>): Promise<Event> {
    const data = await intelliJetNetService.insert<Event>(
      this.TABLE_NAME,
      event,
      { returning: '*' }
    )
    return data[0]
  }

  /**
   * Update event
   */
  static async update(eventId: string, updates: Partial<Event>): Promise<Event> {
    const data = await intelliJetNetService.update<Event>(
      this.TABLE_NAME,
      updates,
      [{ column: 'event_id', operator: 'eq', value: eventId }]
    )
    return data[0]
  }

  /**
   * Soft delete event
   */
  static async softDelete(eventId: string): Promise<void> {
    await this.update(eventId, { is_active: false })
  }
}

export default EventsService